import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { queryKeys } from '@/constant/common'
import {
  allDecodersQueryKey,
  fetchAllDecoders,
  isStationDecoder,
  type DecoderSummary,
} from '@/features/config/hooks/controller/useSearchAllDecoders'
import { buildControllerUpdatePayload } from '@/features/config/pages/Controllers/controllerUpdatePayload'
import { getApiErrorMessage } from '@/helpers/notify'
import { IoTypeMask } from '@/types/controller'
import { resolveIOConfigId } from '@/utils/ioConfigOptions'
import type { QueryClient } from '@tanstack/react-query'

/**
 * SCRUM-1145 — a station's Type and its decoder's purpose are one fact.
 *
 * A station is Irrigation or Non Irrigation, and so is the decoder it sits on.
 * SCRUM-1157 made the station follow its decoder in the wizard; this is the
 * other direction. Changing a station's Type used to leave the decoder as it
 * was, and the wizard then flagged the station red and retyped it back to the
 * decoder — the user's change undone by the very rule meant to keep the two
 * together.
 *
 * So a station written with a Type its decoder does not carry writes the
 * decoder too. Only between the two station purposes: a decoder that is no
 * longer a station decoder at all (repurposed to a pump, say) is a conflict for
 * the user to settle, not something a station edit gets to overwrite.
 */

const NON_IRRIGATION_TYPE = 1
const IRRIGATION_TYPE = 2

const normalise = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

const STATION_PURPOSE_BY_TYPE: Record<number, string> = {
  [NON_IRRIGATION_TYPE]: 'nonirrigation',
  [IRRIGATION_TYPE]: 'irrigation',
}

/** The decoder purpose a station Type calls for, normalised; null if neither. */
const purposeForStationType = (stationType: unknown): string | null =>
  STATION_PURPOSE_BY_TYPE[Number(stationType)] ?? null

const isStationPurpose = (purpose: unknown) =>
  normalise(purpose) === 'irrigation' || normalise(purpose) === 'nonirrigation'

/**
 * Whether writing this station could leave its decoder on the other purpose.
 *
 * Read from the row alone, so the save can skip fetching a thousand decoders
 * when no Type moved. The row's `decoderType` may be stale; `apply` checks the
 * decoder itself before writing anything.
 */
export const needsDecoderPurposeSync = (station: any): boolean => {
  const wanted = purposeForStationType(station?.stationType)
  if (!wanted) return false
  if (!isStationPurpose(station?.decoderType)) return false
  return normalise(station.decoderType) !== wanted
}

/**
 * Whether a station form's save has to look at its decoder at all.
 *
 * The form's decoder option may not carry a purpose; unlike
 * `needsDecoderPurposeSync`, an unknown purpose counts as a maybe, and `apply`
 * reads the decoder to settle it.
 */
export const mayNeedDecoderPurposeSync = (station: any): boolean => {
  const wanted = purposeForStationType(station?.stationType)
  if (!wanted) return false
  if (!station?.decoderAddress) return false
  return normalise(station?.decoderType) !== wanted
}

export interface DecoderPurposeSync {
  /**
   * Writes the decoder's purpose to match the station's Type, if it does not
   * already. Resolves with the purpose name the decoder now has, or null when
   * nothing was written. Throws when the decoder could not be written.
   */
  apply: (station: any) => Promise<string | null>
  /**
   * `apply`, then `writeStation`. If the station write fails, the decoder is
   * put back on the purpose it had and the station's error is rethrown, so a
   * refused station does not leave its decoder on the other type.
   *
   * `undoOn` says which failures the decoder goes back for. A write the client
   * gave up on may still land on the server, and undoing the decoder then
   * would cause the very mismatch this is here to prevent.
   */
  withStation: (
    station: any,
    writeStation: () => Promise<unknown>,
    undoOn?: (error: unknown) => boolean,
  ) => Promise<string | null>
}

/**
 * One per save pass: the decoder list and the purpose ids are read at most
 * once, and only if a station in the pass actually needs its decoder written.
 *
 * Read fresh rather than from the cache. The decoder PUT replaces the whole
 * row, so a stale copy would write back a name or a switch parameter someone
 * has since changed.
 */
export const createDecoderPurposeSync = ({
  interfaceId,
  queryClient,
  notFoundMessage,
  unknownPurposeMessage,
  writeFailedMessage,
}: {
  interfaceId: number | string | null
  queryClient: QueryClient
  /** Shown when the station's decoder is not on the controller. */
  notFoundMessage: string
  /** Shown when the controller has no purpose by the name the Type calls for. */
  unknownPurposeMessage: string
  /**
   * Leads the server's reason when the decoder write itself is refused, so
   * the user can tell it was the decoder, not the station, that failed.
   */
  writeFailedMessage: string
}): DecoderPurposeSync => {
  const iface = Number(interfaceId)
  let decoders: Promise<DecoderSummary[]> | null = null
  let purposes: Promise<{ id: number | string; name?: string }[]> | null = null

  const loadDecoders = () => {
    if (!decoders) {
      decoders = fetchAllDecoders(iface, IoTypeMask.Output)
      // A failed read is not kept for the rest of the pass.
      decoders.catch(() => {
        decoders = null
      })
    }
    return decoders
  }

  const loadPurposes = () => {
    if (!purposes) {
      purposes = controllerDataConfigApi
        .getDecoderType({ interfaceId: iface, PageSize: 100 })
        .then((res: any) => res?.data?.data ?? [])
      purposes.catch(() => {
        purposes = null
      })
    }
    return purposes
  }

  /** Writes `decoder` with the purpose; the pass's copy and the caches follow. */
  const writePurpose = async (
    decoder: DecoderSummary,
    purposeId: number,
    purposeName: string,
  ) => {
    await controllerDataConfigApi.update(
      buildControllerUpdatePayload(decoder, iface, {
        configuration: resolveIOConfigId(
          [],
          undefined,
          (decoder as any).configurationId,
        ),
        decoderType: purposeId,
      }) as any,
    )

    const written = (item: DecoderSummary) =>
      item.id === decoder.id && item.interfaceId === decoder.interfaceId
        ? { ...item, decoderType: purposeName, decoderTypeId: purposeId }
        : item

    // The pass's own copy and the wizard's cached one both take the new
    // purpose straight away. The wizard compares every station against that
    // cache; left stale, it would read the station just written as mismatched
    // and retype it back (SCRUM-1157).
    if (decoders) {
      const current = await decoders
      decoders = Promise.resolve(current.map(written))
    }
    queryClient.setQueryData<DecoderSummary[]>(
      allDecodersQueryKey(iface),
      (cached) => cached?.map(written),
    )
    // The paged decoder grid reads its own query.
    queryClient.invalidateQueries([queryKeys.controller])
  }

  const findPurpose = async (name: string) => {
    const purpose = (await loadPurposes()).find(
      (item) => normalise(item.name) === name,
    )
    const purposeId = Number(purpose?.id)
    if (!purpose || !Number.isFinite(purposeId) || purposeId <= 0)
      throw new Error(unknownPurposeMessage)
    return { purposeId, purposeName: String(purpose.name) }
  }

  /** `apply`, plus a way to put the decoder back as it was found. */
  const applyWithUndo = async (
    station: any,
  ): Promise<{ purpose: string | null; undo?: () => Promise<void> }> => {
    const wanted = purposeForStationType(station?.stationType)
    if (!wanted) return { purpose: null }

    const address = String(station?.decoderAddress ?? '')
    const all = await loadDecoders()
    // On a control hub two sub-controllers can share an address-index; the
    // station's own twiId settles which one it means when it has one.
    const byId = station?.decoderId
      ? all.find((item) => item.id === station.decoderId)
      : undefined
    const byAddress = byId
      ? []
      : all.filter(
          (item) =>
            `${item.address}-${item.index}` === address &&
            (station?.twiId == null || item.twiId == null || item.twiId === station.twiId),
        )
    // Two sub-controllers of a hub with this address and nothing on the row to
    // tell them apart: writing either could change the other station's decoder.
    if (!byId && byAddress.length > 1) return { purpose: null }
    const decoder = byId ?? byAddress[0]
    if (!decoder) throw new Error(notFoundMessage)

    // Repurposed to something that is not a station: the station is the one in
    // the wrong, and the user decides which way to fix it.
    if (!isStationDecoder(decoder)) return { purpose: null }
    if (normalise(decoder.decoderType) === wanted) return { purpose: null }

    const { purposeId, purposeName } = await findPurpose(wanted)
    // Looked up before the write, so a decoder that could not be put back is
    // never written in the first place.
    const before = await findPurpose(normalise(decoder.decoderType))

    try {
      await writePurpose(decoder, purposeId, purposeName)
    } catch (error) {
      throw new Error(`${writeFailedMessage} (${getApiErrorMessage(error)})`)
    }

    return {
      purpose: purposeName,
      undo: () => writePurpose(decoder, before.purposeId, before.purposeName),
    }
  }

  const apply = async (station: any) => (await applyWithUndo(station)).purpose

  const withStation = async (
    station: any,
    writeStation: () => Promise<unknown>,
    undoOn: (error: unknown) => boolean = () => true,
  ) => {
    const { purpose, undo } = await applyWithUndo(station)
    try {
      await writeStation()
    } catch (error) {
      if (undo && undoOn(error)) {
        try {
          await undo()
        } catch (undoError) {
          // Left mismatched; the controller list flags the decoder until the
          // two agree again.
          console.error('Decoder purpose rollback failed:', undoError)
        }
      }
      throw error
    }
    return purpose
  }

  return { apply, withStation }
}
